import React, { useState } from 'react';
import { FiArrowLeft, FiExternalLink, FiFileText, FiLoader, FiTarget, FiTrendingUp, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import EnhancedMarkdown from '../CP-Search/EnhancedMarkDown';
import SourceAnalysisMarkdown from './SourceAnalysisMarkdown';
import SourceAnalysisService from './SourceAnalysisService';

/**
 * Composant pour afficher l'indicateur de pertinence d'une source
 */
const RelevanceIndicator = ({ score }) => {
  if (!score && score !== 0) return null;
  
  const percentage = Math.round(score * 100);
  
  // Déterminer la couleur selon le score
  let colorClass = 'bg-green-500';
  let bgColorClass = 'bg-green-100';
  let textColorClass = 'text-green-700';
  
  if (percentage < 60) {
    colorClass = 'bg-orange-500';
    bgColorClass = 'bg-orange-100';
    textColorClass = 'text-orange-700';
  } else if (percentage < 80) {
    colorClass = 'bg-yellow-500';
    bgColorClass = 'bg-yellow-100';
    textColorClass = 'text-yellow-700';
  }
  
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">Pertinence</span>
          <span className={`text-xs font-semibold ${textColorClass}`}>{percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`${colorClass} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <span className={`px-2 py-1 ${bgColorClass} ${textColorClass} rounded-lg text-xs font-semibold`}>
        {percentage}%
      </span>
    </div>
  );
};

/**
 * Composant pour afficher l'objectif avec troncature et expansion
 */
const ObjectiveDisplay = ({ objective }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!objective) return null;
  
  // Vérifier si l'objectif est long (plus de 100 caractères)
  const isLongObjective = objective.length > 100;
  
  return (
    <div className="relative">
      <div 
        className={`text-2xl font-bold text-gray-900 mb-2 transition-all duration-300 ${
          isLongObjective && !isExpanded ? 'line-clamp-2' : ''
        }`}
      >
        {objective}
      </div>
      
      {isLongObjective && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors mt-1"
        >
          {isExpanded ? (
            <>
              <FiChevronUp size={14} />
              Voir moins
            </>
          ) : (
            <>
              <FiChevronDown size={14} />
              Voir plus
            </>
          )}
        </button>
      )}
    </div>
  );
};

/**
 * Composant pour visualiser une synthèse de recherche
 */
const SynthesisViewer = ({ synthesis, onBack, userLanguage = 'fr' }) => {
  const [expandedSources, setExpandedSources] = useState({});
  const [loadingAnalysis, setLoadingAnalysis] = useState({});
  const [sourceAnalyses, setSourceAnalyses] = useState({});

  const toggleSource = (index) => {
    setExpandedSources(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const generateDetailedAnalysis = async (source, index) => {
    if (sourceAnalyses[index]) {
      // Si l'analyse existe déjà, on l'affiche/cache
      setExpandedSources(prev => ({
        ...prev,
        [index]: !prev[index]
      }));
      return;
    }

    try {
      setLoadingAnalysis(prev => ({ ...prev, [index]: true }));
      
      const analysis = await SourceAnalysisService.generateDetailedSummary(
        source,
        synthesis.searchObjective,
        userLanguage
      );
      
      // Vérifier si l'analyse est null (échec de Perplexity et JinaAI)
      if (!analysis) {
        // Stocker un message d'erreur spécial
        setSourceAnalyses(prev => ({
          ...prev,
          [index]: 'ERROR_NO_EXTRACTION'
        }));
        
        setExpandedSources(prev => ({
          ...prev,
          [index]: true
        }));
        return;
      }
      
      setSourceAnalyses(prev => ({
        ...prev,
        [index]: analysis
      }));
      
      setExpandedSources(prev => ({
        ...prev,
        [index]: true
      }));
    } catch (error) {
      console.error('Erreur lors de la génération de l\'analyse:', error);
      // Stocker un message d'erreur générique
      setSourceAnalyses(prev => ({
        ...prev,
        [index]: 'ERROR_GENERIC'
      }));
      
      setExpandedSources(prev => ({
        ...prev,
        [index]: true
      }));
    } finally {
      setLoadingAnalysis(prev => ({ ...prev, [index]: false }));
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* En-tête */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-4 transition-colors"
        >
          <FiArrowLeft className="mr-2" />
          Retour à la liste
        </button>
        
        {/* Titre personnalisé avec icône */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiTarget className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <ObjectiveDisplay objective={synthesis.searchObjective || synthesis.title || 'Synthèse de recherche'} />
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <FiTrendingUp className="h-4 w-4" />
                  {(synthesis.sourcesUsed || synthesis.sources)?.length || 0} sources
                </span>
                <span>•</span>
                <span>Créée le {formatDate(synthesis.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu de la synthèse avec formatage amélioré */}
      {synthesis.content && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiFileText className="h-5 w-5 text-indigo-600" />
            Synthèse complète
          </h2>
          <div className="prose max-w-none">
            <EnhancedMarkdown content={synthesis.content} />
          </div>
        </div>
      )}

      {/* Sources avec affichage amélioré */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FiFileText className="h-6 w-6 text-blue-600" />
          Sources ({(synthesis.sourcesUsed || synthesis.sources)?.length || 0})
        </h2>

        {(synthesis.sourcesUsed || synthesis.sources) && (synthesis.sourcesUsed || synthesis.sources).length > 0 ? (
          (synthesis.sourcesUsed || synthesis.sources).map((source, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* En-tête de la source */}
              <div className="p-6">
                {/* Titre */}
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {source.title}
                </h3>

                {/* Indicateur de pertinence */}
                <RelevanceIndicator score={source.score || source.relevance} />

                {/* Domaine et métadonnées */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {source.domain && (
                    <span className="text-sm text-gray-600 font-medium">
                      {source.domain}
                    </span>
                  )}
                  {source.sourceCategory && (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">
                      {source.sourceCategory}
                    </span>
                  )}
                  {source.language && (
                    <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-medium">
                      {source.language.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Description de la source - Plus visible */}
                {source.description && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {source.description}
                    </p>
                  </div>
                )}

                {/* Résumé (snippet) - Mieux formaté */}
                {source.snippet && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                    <p className="text-gray-600 text-sm italic leading-relaxed">
                      "{source.snippet}"
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Lien vers la source - Plus discret */}
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    <FiExternalLink size={14} />
                    Consulter la source
                  </a>

                  <span className="text-gray-300">•</span>

                  {/* Bouton pour générer/afficher l'analyse détaillée */}
                  <button
                    onClick={() => generateDetailedAnalysis(source, index)}
                    disabled={loadingAnalysis[index]}
                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {loadingAnalysis[index] ? (
                      <>
                        <FiLoader className="animate-spin" size={14} />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <FiFileText size={14} />
                        {sourceAnalyses[index] 
                          ? (expandedSources[index] ? 'Masquer' : 'Afficher') + ' l\'analyse détaillée'
                          : 'Générer une analyse détaillée'
                        }
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Analyse détaillée */}
              {expandedSources[index] && sourceAnalyses[index] && (
                <div className="border-t border-gray-200 bg-gray-50 p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Analyse détaillée</h4>
                  
                  {/* Gestion des erreurs */}
                  {sourceAnalyses[index] === 'ERROR_NO_EXTRACTION' ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-sm font-semibold text-orange-800 mb-2">
                            Impossible d'extraire le contenu de cette source
                          </h5>
                          <p className="text-sm text-orange-700 mb-3">
                            Nos services d'extraction (Perplexity et JinaAI) n'ont pas pu accéder au contenu de cette page. Cela peut être dû à :
                          </p>
                          <ul className="text-sm text-orange-700 space-y-1 mb-3 list-disc list-inside">
                            <li>La source n'est pas accessible publiquement</li>
                            <li>Le contenu est protégé par un paywall ou une authentification</li>
                            <li>Problème temporaire de connexion</li>
                            <li>Le site bloque les robots d'extraction</li>
                          </ul>
                          <div className="flex items-center gap-2 mt-3">
                            <FiExternalLink className="h-4 w-4 text-orange-600" />
                            <a
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-orange-600 hover:text-orange-700 underline"
                            >
                              Consulter directement la source
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : sourceAnalyses[index] === 'ERROR_GENERIC' ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-sm font-semibold text-red-800 mb-2">
                            Erreur lors de la génération de l'analyse
                          </h5>
                          <p className="text-sm text-red-700 mb-3">
                            Une erreur technique s'est produite lors de la génération de l'analyse détaillée. Veuillez réessayer dans quelques instants.
                          </p>
                          <button
                            onClick={() => {
                              // Réinitialiser l'analyse pour permettre une nouvelle tentative
                              setSourceAnalyses(prev => {
                                const newAnalyses = { ...prev };
                                delete newAnalyses[index];
                                return newAnalyses;
                              });
                              setExpandedSources(prev => ({
                                ...prev,
                                [index]: false
                              }));
                            }}
                            className="text-sm font-medium text-red-600 hover:text-red-700 underline"
                          >
                            Réessayer
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="prose max-w-none">
                      <SourceAnalysisMarkdown content={sourceAnalyses[index]} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            Aucune source disponible
          </div>
        )}
      </div>

      {/* Métadonnées */}
      {synthesis.metadata && (
        <div className="mt-8 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Informations complémentaires
          </h3>
          <div className="space-y-2 text-sm text-gray-600">
            {synthesis.metadata.language && (
              <p><strong>Langue :</strong> {synthesis.metadata.language}</p>
            )}
            {synthesis.metadata.searchQuery && (
              <p><strong>Requête de recherche :</strong> {synthesis.metadata.searchQuery}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SynthesisViewer;
